// web-admin/src/pages/sm/StoreSetupPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Switch,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Slider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  Popup,
  useMap,
  useMapEvents
} from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import AppHeader from '../../components/AppHeader';

import StorefrontIcon from '@mui/icons-material/Storefront';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RefreshIcon from '@mui/icons-material/Refresh';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import MapIcon from '@mui/icons-material/Map';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Supported Map Tile Providers (Google Maps, Satellite, OpenStreetMap)
const MAP_LAYERS = {
  google_streets: {
    label: 'Google Map',
    url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    attribution: '&copy; Google Maps',
    maxZoom: 20
  },
  google_hybrid: {
    label: 'Satellite',
    url: 'https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    attribution: '&copy; Google Maps Satellite',
    maxZoom: 20
  },
  osm: {
    label: 'OSM',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    subdomains: ['a', 'b', 'c'],
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19
  }
};

const storeMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const selectedPickerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const API_BASE = process.env.REACT_APP_API_URL1 || 'http://localhost:3000/api';

const getBearerToken = () =>
  localStorage.getItem('userToken') ||
  localStorage.getItem('token') ||
  sessionStorage.getItem('userToken') ||
  sessionStorage.getItem('token') ||
  '';

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getBearerToken()}`,
    'X-Tenant': (localStorage.getItem('tenant') || '').trim()
  }
});

// Component to handle map clicks for coordinate selection
const LocationPicker = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Component to auto-center map
const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Component to handle dynamic resizing of Leaflet container
const MapResizer = ({ trigger }) => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    return () => clearTimeout(timer);
  }, [trigger, map]);
  return null;
};

const StoreSetupPage = ({ setIsLoggedIn }) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [showTableInFullMap, setShowTableInFullMap] = useState(false);
  const [mapLayer, setMapLayer] = useState('google_streets');

  // Dialog State (Add / Edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    store_code: '',
    store_name: '',
    latitude: 16.8661,
    longitude: 96.1951,
    radius_meters: 30,
    address: '',
    township: '',
    contact_person: '',
    phone_number: '',
    is_active: 1
  });

  const [activeStoreFocus, setActiveStoreFocus] = useState(null);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/checkpoints/stores?include_inactive=true`, getHeaders());
      if (res.data?.success) {
        setStores(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch stores:', err);
      setError('Failed to load store list.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    setFormData({
      id: null,
      store_code: `STR-${Date.now().toString().slice(-4)}`,
      store_name: '',
      latitude: activeStoreFocus ? activeStoreFocus.latitude : 16.8661,
      longitude: activeStoreFocus ? activeStoreFocus.longitude : 96.1951,
      radius_meters: 30,
      address: '',
      township: '',
      contact_person: '',
      phone_number: '',
      is_active: 1
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (store) => {
    setIsEditing(true);
    setFormData({
      id: store.id,
      store_code: store.store_code,
      store_name: store.store_name,
      latitude: parseFloat(store.latitude),
      longitude: parseFloat(store.longitude),
      radius_meters: parseInt(store.radius_meters, 10) || 30,
      address: store.address || '',
      township: store.township || '',
      contact_person: store.contact_person || '',
      phone_number: store.phone_number || '',
      is_active: store.is_active
    });
    setDialogOpen(true);
  };

  const handleSaveStore = async () => {
    if (!formData.store_name.trim()) {
      alert('Store Name is required.');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API_BASE}/checkpoints/stores/${formData.id}`, formData, getHeaders());
        setSuccessMsg(`Store "${formData.store_name}" updated successfully.`);
      } else {
        await axios.post(`${API_BASE}/checkpoints/stores`, formData, getHeaders());
        setSuccessMsg(`Store "${formData.store_name}" added successfully.`);
      }
      setDialogOpen(false);
      fetchStores();
    } catch (err) {
      console.error('Save store error:', err);
      alert(err.response?.data?.message || 'Failed to save store.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (store) => {
    try {
      const newStatus = store.is_active ? 0 : 1;
      await axios.put(
        `${API_BASE}/checkpoints/stores/${store.id}`,
        { is_active: newStatus },
        getHeaders()
      );
      setStores(prev =>
        prev.map(s => (s.id === store.id ? { ...s, is_active: newStatus } : s))
      );
    } catch (err) {
      console.error('Toggle store active error:', err);
      alert('Failed to update store status.');
    }
  };

  const filteredStores = stores.filter(s => {
    const term = search.toLowerCase();
    return (
      (s.store_name || '').toLowerCase().includes(term) ||
      (s.store_code || '').toLowerCase().includes(term) ||
      (s.township || '').toLowerCase().includes(term) ||
      (s.address || '').toLowerCase().includes(term)
    );
  });

  const mapCenter = activeStoreFocus
    ? [parseFloat(activeStoreFocus.latitude), parseFloat(activeStoreFocus.longitude)]
    : stores.length > 0
    ? [parseFloat(stores[0].latitude), parseFloat(stores[0].longitude)]
    : [16.8661, 96.1951];

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f4f6f8', minHeight: '100vh', pb: 4, width: '100%', maxWidth: '100vw', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ px: 3, mt: 3, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Header Title & Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StorefrontIcon color="primary" sx={{ fontSize: 34 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="text.primary">
                Store & Outlet Setup
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure client stores, GPS geofences, and check-in radius boundaries for field staff.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<MapIcon />}
              onClick={() => window.location.href = '/field-ops/route-map'}
            >
              Field Route Map
            </Button>
            <Button
              variant="contained"
              startIcon={<AddLocationAltIcon />}
              onClick={handleOpenAdd}
              sx={{ fontWeight: 'bold' }}
            >
              Add New Store
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
            {successMsg}
          </Alert>
        )}

        {/* Responsive Layout: Left (Store List) / Right (Map with Geofence Circles) */}
        <Grid container spacing={3}>
          {/* Left Column: Stores Table (Hidden or Top Accordion in Full Width mode, otherwise 35%-37% width) */}
          {(!isMapExpanded || showTableInFullMap) && (
            <Grid item xs={12} lg={isMapExpanded ? 12 : 4.5} xl={isMapExpanded ? 12 : 4}>
              <Paper sx={{ p: 2, borderRadius: 2, minHeight: isMapExpanded ? 380 : 700, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by store name, code, township..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip title="Refresh Stores">
                    <IconButton onClick={fetchStores} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Total Stores: <strong>{filteredStores.length}</strong> | Click a store to view its geofence on the map.
                </Typography>

                {loading ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CircularProgress size={30} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading stores...
                    </Typography>
                  </Box>
                ) : filteredStores.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <StorefrontIcon sx={{ fontSize: 48, color: '#ccc' }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                      No stores found. Click "Add New Store" to register your first outlet.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ flexGrow: 1, maxHeight: isMapExpanded ? 320 : 580 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Store / Code</TableCell>
                          <TableCell>Township / Address</TableCell>
                          <TableCell align="center">Radius</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredStores.map(store => {
                          const isFocused = activeStoreFocus?.id === store.id;
                          return (
                            <TableRow
                              key={store.id}
                              hover
                              selected={isFocused}
                              onClick={() => setActiveStoreFocus(store)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {store.store_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {store.store_code}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2">
                                  {store.township || '—'}
                                </Typography>
                                {store.address && (
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                                    {store.address}
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell align="center">
                                <Chip
                                  label={`${store.radius_meters || 30}m`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>

                              <TableCell align="center">
                                <Switch
                                  size="small"
                                  checked={Boolean(store.is_active)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggleActive(store);
                                  }}
                                  color="success"
                                />
                              </TableCell>

                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEdit(store);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          )}

          {/* Right Column: Interactive Map & Visual Geofence Boundaries (Expanded Width: 65% by default, up to 100% full width) */}
          <Grid item xs={12} lg={isMapExpanded ? 12 : 7.5} xl={isMapExpanded ? 12 : 8}>
            <Paper sx={{ p: 2, borderRadius: 2, height: isMapExpanded ? 780 : 700, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🗺️ Geofence Boundary Visualization
                  </Typography>
                  {activeStoreFocus && (
                    <Chip
                      icon={<FmdGoodIcon />}
                      label={`Viewing: ${activeStoreFocus.store_name}`}
                      color="success"
                      size="small"
                      onDelete={() => setActiveStoreFocus(null)}
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {/* Google Map / Satellite / OSM layer switcher */}
                  <ToggleButtonGroup
                    value={mapLayer}
                    exclusive
                    onChange={(_, newLayer) => {
                      if (newLayer) setMapLayer(newLayer);
                    }}
                    size="small"
                    sx={{
                      bgcolor: '#fff',
                      height: 32,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      '& .MuiToggleButton-root': {
                        px: 1.2,
                        py: 0.2,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'none'
                      }
                    }}
                  >
                    <ToggleButton value="google_streets">🌐 Google Map</ToggleButton>
                    <ToggleButton value="google_hybrid">🛰️ Satellite</ToggleButton>
                    <ToggleButton value="osm">🗺️ OSM</ToggleButton>
                  </ToggleButtonGroup>

                  {isMapExpanded && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowTableInFullMap(!showTableInFullMap)}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', height: 32 }}
                    >
                      {showTableInFullMap ? 'Hide Store Table' : 'Show Store Table'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant={isMapExpanded ? "contained" : "outlined"}
                    color="primary"
                    startIcon={isMapExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    onClick={() => setIsMapExpanded(!isMapExpanded)}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', height: 32 }}
                  >
                    {isMapExpanded ? 'Collapse to Split View' : 'Expand Full Width (100%)'}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ flexGrow: 1, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0', position: 'relative' }}>
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  style={{ width: '100%', height: '100%' }}
                >
                  <TileLayer
                    key={mapLayer}
                    attribution={MAP_LAYERS[mapLayer].attribution}
                    url={MAP_LAYERS[mapLayer].url}
                    subdomains={MAP_LAYERS[mapLayer].subdomains}
                    maxZoom={MAP_LAYERS[mapLayer].maxZoom}
                  />

                  <RecenterMap center={mapCenter} />
                  <MapResizer trigger={isMapExpanded} />

                  {/* Render All Stores with Geofence Circles */}
                  {filteredStores.map(store => {
                    const lat = parseFloat(store.latitude);
                    const lng = parseFloat(store.longitude);
                    const radius = parseInt(store.radius_meters, 10) || 30;
                    const isFocused = activeStoreFocus?.id === store.id;

                    if (isNaN(lat) || isNaN(lng)) return null;

                    return (
                      <React.Fragment key={store.id}>
                        {/* Circle boundary representing store geofence */}
                        <Circle
                          center={[lat, lng]}
                          radius={radius}
                          pathOptions={{
                            color: isFocused ? '#d32f2f' : '#2e7d32',
                            fillColor: isFocused ? '#f44336' : '#4caf50',
                            fillOpacity: isFocused ? 0.35 : 0.2,
                            weight: isFocused ? 2 : 1
                          }}
                        />

                        {/* Store Marker */}
                        <Marker
                          position={[lat, lng]}
                          icon={isFocused ? selectedPickerIcon : storeMarkerIcon}
                          eventHandlers={{
                            click: () => setActiveStoreFocus(store)
                          }}
                        >
                          <Popup>
                            <Box sx={{ minWidth: 210, p: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                🏪 {store.store_name}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Code: {store.store_code}
                              </Typography>
                              <Typography variant="caption" display="block">
                                🎯 Geofence Radius: <strong>{radius} meters</strong>
                              </Typography>
                              <Typography variant="caption" display="block">
                                📍 {lat.toFixed(5)}, {lng.toFixed(5)}
                              </Typography>
                              {store.address && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  🏠 {store.address} {store.township ? `(${store.township})` : ''}
                                </Typography>
                              )}
                              {store.contact_person && (
                                <Typography variant="caption" display="block">
                                  👤 {store.contact_person} {store.phone_number ? `• ${store.phone_number}` : ''}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ flex: 1, textTransform: 'none', fontSize: '0.75rem' }}
                                  onClick={() => handleOpenEdit(store)}
                                >
                                  Edit Store
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  startIcon={<OpenInNewIcon fontSize="small" />}
                                  href={`https://www.google.com/maps?q=${lat},${lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ flex: 1.2, textTransform: 'none', fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                                >
                                  Google Maps
                                </Button>
                              </Box>
                            </Box>
                          </Popup>
                        </Marker>
                      </React.Fragment>
                    );
                  })}
                </MapContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Add / Edit Store Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {isEditing ? `Edit Store: ${formData.store_name}` : 'Add New Client Store / Outlet'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Store Code"
                value={formData.store_code}
                onChange={e => setFormData({ ...formData, store_code: e.target.value })}
                helperText="Unique identifier (e.g. STR-001)"
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                required
                label="Store Name"
                value={formData.store_name}
                onChange={e => setFormData({ ...formData, store_name: e.target.value })}
                placeholder="e.g. City Mart (Junction City Branch)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Township / Area"
                value={formData.township}
                onChange={e => setFormData({ ...formData, township: e.target.value })}
                placeholder="e.g. Pabedan, Kamayut"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Contact Person"
                value={formData.contact_person}
                onChange={e => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="Store Manager / Contact Person"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                value={formData.phone_number}
                onChange={e => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="09..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <Typography variant="caption" fontWeight="bold" display="block">
                  🎯 Geofence Radius: {formData.radius_meters} meters
                </Typography>
                <Slider
                  value={formData.radius_meters}
                  min={30}
                  max={200}
                  step={5}
                  valueLabelDisplay="auto"
                  onChange={(_, val) => setFormData({ ...formData, radius_meters: val })}
                />
                <Typography variant="caption" color="text.secondary">
                  Minimum 30m recommended for reliable mobile GPS detection.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Store Address & Floor/Unit Details"
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
                placeholder="Unit #12, Ground Floor, Corner of Bogyoke Rd..."
                helperText="Helpful for side-by-side stores (e.g. Unit 12 vs Unit 14)"
              />
            </Grid>

            {/* Interactive Coordinate Picker Map inside Dialog */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  📍 Click on the map to place store pin:
                </Typography>
                <ToggleButtonGroup
                  value={mapLayer}
                  exclusive
                  onChange={(_, newLayer) => {
                    if (newLayer) setMapLayer(newLayer);
                  }}
                  size="small"
                  sx={{
                    height: 28,
                    '& .MuiToggleButton-root': {
                      px: 1,
                      py: 0.2,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'none'
                    }
                  }}
                >
                  <ToggleButton value="google_streets">🌐 Google</ToggleButton>
                  <ToggleButton value="google_hybrid">🛰️ Satellite</ToggleButton>
                  <ToggleButton value="osm">🗺️ OSM</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ height: 260, borderRadius: 1, overflow: 'hidden', border: '1px solid #ccc' }}>
                <MapContainer
                  center={[formData.latitude, formData.longitude]}
                  zoom={16}
                  style={{ width: '100%', height: '100%' }}
                >
                  <TileLayer
                    key={`dialog-${mapLayer}`}
                    attribution={MAP_LAYERS[mapLayer].attribution}
                    url={MAP_LAYERS[mapLayer].url}
                    subdomains={MAP_LAYERS[mapLayer].subdomains}
                    maxZoom={MAP_LAYERS[mapLayer].maxZoom}
                  />
                  <LocationPicker
                    onLocationSelect={(lat, lng) => {
                      setFormData(prev => ({
                        ...prev,
                        latitude: parseFloat(lat.toFixed(7)),
                        longitude: parseFloat(lng.toFixed(7))
                      }));
                    }}
                  />
                  <RecenterMap center={[formData.latitude, formData.longitude]} />
                  <Circle
                    center={[formData.latitude, formData.longitude]}
                    radius={formData.radius_meters}
                    pathOptions={{ color: '#d32f2f', fillColor: '#f44336', fillOpacity: 0.25 }}
                  />
                  <Marker
                    position={[formData.latitude, formData.longitude]}
                    icon={selectedPickerIcon}
                  />
                </MapContainer>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <TextField
                  size="small"
                  label="Latitude"
                  type="number"
                  value={formData.latitude}
                  onChange={e => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Longitude"
                  type="number"
                  value={formData.longitude}
                  onChange={e => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<MyLocationIcon />}
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition((pos) => {
                        setFormData(prev => ({
                          ...prev,
                          latitude: parseFloat(pos.coords.latitude.toFixed(7)),
                          longitude: parseFloat(pos.coords.longitude.toFixed(7))
                        }));
                      });
                    }
                  }}
                >
                  My Location
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveStore} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : isEditing ? 'Save Changes' : 'Create Store'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreSetupPage;
